import { Fragment, useState } from 'react';
import {
  formatUserFacingCutoffDate,
  getCutoffTimeZoneAbbreviation,
} from '@hungryroot/datetime';
import { format, parseISO } from 'date-fns';
import Selector from '../generic/Selector';
import { createSelectorOptions } from '../utils/utils';
import Modal, { GenericBaseModal } from '../generic/Modal';
import { skipOrder, rescheduleOrder } from '../api/api';

const ModalContent = ({
  order,
  closeModal,
  onSkipOrder,
  onRescheduleOrder,
  showWarning,
  showWarningModal = false,
}) => {
  let [selectedDate, setSelectedDate] = useState(order.preferred_date);
  let [newOrderCutoff, setNewOrderCutoff] = useState('');
  let [warningModalVisible, setWarningModalVisibility] =
    useState(showWarningModal);
  let [skipping, setSkipping] = useState(false);
  const deliveryDatesCount =
    order.cutoff_a_delivery_dates.length + order.cutoff_b_delivery_dates.length;

  const createDateLabel = (date) => {
    return formatUserFacingCutoffDate(date, 'EEEE, MMMM do');
  };

  const createBoldedDateLabel = (date) => {
    const day = format(parseISO(date), 'EEEE');
    const monthDate = format(parseISO(date), 'MMMM do');
    return `<span class="hr-paragraph-bold">${day}</span>, ${monthDate}`;
  };

  const cutoffADateOptions = order.cutoff_a_delivery_dates.map((date) => {
    return {
      value: date,
      label: createBoldedDateLabel(date),
    };
  });

  const cutoffBDateOptions = order.cutoff_b_delivery_dates.map((date) => {
    return {
      value: date,
      label: createBoldedDateLabel(date),
    };
  });

  const skipOrderAndCloseModal = () => {
    setSkipping(true);
    closeModal();
    return skipOrder(order.id)
      .then((res) => {
        onSkipOrder(order, res.status);
      })
      .catch(console.error);
  };

  const rescheduleOrderAndCloseModal = () => {
    rescheduleOrder(order.id, selectedDate)
      .then((res) => {
        if (res.updated) {
          onRescheduleOrder(order, selectedDate, newOrderCutoff);
        }
      })
      .catch(console.error);
    closeModal();
  };

  const openWarningModal = () => {
    setWarningModalVisibility(true);
  };

  const closeModalAndEditDelivery = () => {
    closeModal();
    if (!skipping) {
      window.location = `/edit-delivery/featured/${order.id}/`;
    }
  };

  const setSelectedDayAndCutoff = (date, cutoff) => {
    setSelectedDate(date);
    setNewOrderCutoff(cutoff);
  };

  if (warningModalVisible) {
    return (
      <GenericBaseModal
        description={`Are you sure you want to skip this week? If you don't like the groceries or recipes in your delivery, you can always edit.`}
        closeText={'EDIT DELIVERY'}
        actionText={'YES, SKIP IT'}
        action={skipOrderAndCloseModal}
        closeModal={closeModalAndEditDelivery}
      />
    );
  }

  if (!order.in_seven_day_period) {
    const dateOptions = cutoffADateOptions.concat(cutoffBDateOptions);
    return (
      <div>
        <div className="hr-title-sm hr-mb-24">Reschedule this delivery</div>
        <div className="hr-subtitle-md hr-mb-8">
          Edit by {createDateLabel(order.cutoff_date)} at{' '}
          {formatUserFacingCutoffDate(order.cutoff_date, 'haaa')}{' '}
          {getCutoffTimeZoneAbbreviation()}
        </div>
        <div className="hr-mb-32_fixed">
          <Selector
            type="radio"
            options={createSelectorOptions(
              dateOptions,
              'value',
              'label',
              null,
              null,
              { optionHTML: true }
            )}
            selected={selectedDate}
            action={(date) =>
              setSelectedDayAndCutoff(date, order.possible_cutoffs[1])
            }
          />
        </div>
        <div
          className="hr-btn-blue hr-max hr-mb-8"
          onClick={rescheduleOrderAndCloseModal}
        >
          {deliveryDatesCount <= 1 ? 'close' : 'Reschedule Delivery'}
        </div>
        {order.sequence !== 1 ? (
          <div
            className="hr-btn-hol hr-max"
            onClick={showWarning ? openWarningModal : skipOrderAndCloseModal}
          >
            Skip Delivery
          </div>
        ) : null}
        <span onClick={closeModal} className="close"></span>
      </div>
    );
  }

  return (
    <div data-testid="modal-reschedule-delivery">
      <div className="hr-title-sm hr-mb-24">Reschedule this delivery</div>
      {order.cutoff_a_delivery_dates.length > 0 ? (
        <Fragment>
          <div className="hr-subtitle-md hr-mb-8">
            Edit by {createDateLabel(order.possible_cutoffs[0])} at{' '}
            {formatUserFacingCutoffDate(order.possible_cutoffs[0], 'haaa')}{' '}
            {getCutoffTimeZoneAbbreviation()}
          </div>
          <div className="hr-mb-24">
            <Selector
              type="radio"
              options={createSelectorOptions(
                cutoffADateOptions,
                'value',
                'label',
                null,
                null,
                { optionHTML: true }
              )}
              selected={selectedDate}
              action={(date) =>
                setSelectedDayAndCutoff(date, order.possible_cutoffs[0])
              }
            />
          </div>
        </Fragment>
      ) : null}
      {order.cutoff_b_delivery_dates.length > 0 ? (
        <Fragment>
          <div className="hr-subtitle-md hr-mb-8">
            Edit by {createDateLabel(order.possible_cutoffs[1])} at{' '}
            {formatUserFacingCutoffDate(order.possible_cutoffs[1], 'haaa')}{' '}
            {getCutoffTimeZoneAbbreviation()}
          </div>
          <div className="hr-mb-24">
            <Selector
              type="radio"
              options={createSelectorOptions(
                cutoffBDateOptions,
                'value',
                'label',
                null,
                null,
                { optionHTML: true }
              )}
              selected={selectedDate}
              action={(date) =>
                setSelectedDayAndCutoff(date, order.possible_cutoffs[1])
              }
            />
          </div>
        </Fragment>
      ) : null}
      <div
        data-testid="rescheduler-modal-button-reschedule"
        className="hr-btn-blue hr-max hr-mb-8"
        onClick={rescheduleOrderAndCloseModal}
      >
        {deliveryDatesCount <= 1 ? 'close' : 'Reschedule Delivery'}
      </div>
      {order.sequence !== 1 ? (
        <div
          className="hr-btn-hol hr-max"
          onClick={showWarning ? openWarningModal : skipOrderAndCloseModal}
        >
          Skip Delivery
        </div>
      ) : null}
      <span
        data-testid="rescheduler-modal-close"
        onClick={closeModal}
        className="close"
      ></span>
    </div>
  );
};

const ReschedulerModal = ({
  modalVisible,
  onModalClose,
  order,
  onSkipOrder,
  onRescheduleOrder,
  showWarning,
  showWarningModal = false,
}) => {
  return (
    <Modal
      visible={modalVisible}
      onClose={() => onModalClose()}
      extraClassName="rescheduler-modal"
    >
      <ModalContent
        order={order}
        onSkipOrder={onSkipOrder}
        onRescheduleOrder={onRescheduleOrder}
        showWarning={showWarning}
        showWarningModal={showWarningModal}
      />
    </Modal>
  );
};

export default ReschedulerModal;
